// src/components/TablaDetalles.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import * as XLSX from 'xlsx';

const TablaDetalles = ({ identificacion = '' }) => {
    const [detalles, setDetalles] = useState([]);
    const [codigosComerciales, setCodigosComerciales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingCodigos, setLoadingCodigos] = useState(false);
    const [error, setError] = useState(null);
    const [, setStats] = useState({ total: 0, mostrados: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const tableContainerRef = useRef(null);
    const [orden, setOrden] = useState({ campo: 'fechaEmision', direccion: 'desc' });
    const [filtroActivo, setFiltroActivo] = useState("Sin filtro de Emisor ID");
    const [documentosActualizados, setDocumentosActualizados] = useState(0);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [documentoSeleccionado, setDocumentoSeleccionado] = useState(null);
    const [codigoSeleccionado, setCodigoSeleccionado] = useState('');
    const [filtroCodigo, setFiltroCodigo] = useState('');

    const estructuraCampos = [
        { key: 'numeroConsecutivo', label: 'N° Consecutivo', width: '120px' },
        { key: 'codigoEmpresa', label: 'Código Empresa', width: '140px' },
        { key: 'emisorNumeroIdentificacion', label: 'Emisor ID', width: '150px' },
        { key: 'emisorNombre', label: 'Emisor Nombre', width: '200px' },
        { key: 'receptorNumeroIdentificacion', label: 'Receptor ID', width: '150px' },
        { key: 'receptorNombre', label: 'Receptor Nombre', width: '200px' },
        { key: 'fechaEmision', label: 'Fecha Emisión', width: '120px' },
        { key: 'codigoMoneda', label: 'Código Moneda', width: '100px' },
        { key: 'tipoCambio', label: 'Tipo Cambio', width: '100px' },
        { key: 'numeroLinea', label: 'N° Línea', width: '80px' },
        { key: 'codigo', label: 'Código CAByS', width: '100px' },
        { key: 'codigoComercialTipo', label: 'Tipo Comercial', width: '120px' },
        { key: 'codigoComercialCodigo', label: 'Código Comercial', width: '120px' },
        { key: 'cantidad', label: 'Cantidad', width: '80px' },
        { key: 'unidadMedida', label: 'Unidad Medida', width: '120px' },
        { key: 'detalle', label: 'Detalle', width: '200px' },
        { key: 'precioUnitario', label: 'Precio Unitario', width: '120px' },
        { key: 'montoTotal', label: 'Monto Total', width: '120px' },
        { key: 'subTotal', label: 'Subtotal', width: '120px' },
        { key: 'impuestoCodigo', label: 'Código Impuesto', width: '120px' },
        { key: 'impuestoCodigoTarifa', label: 'Tarifa Impuesto', width: '120px' },
        { key: 'impuestoTarifa', label: 'Tarifa', width: '80px' },
        { key: 'impuestoMonto', label: 'Monto Impuesto', width: '120px' },
        { key: 'montoTotalLinea', label: 'Total Línea', width: '120px' }
    ];

    useEffect(() => {
        console.log('🔍 Identificación recibida en TablaDetalles:', identificacion);
    }, [identificacion]);

    // Función para buscar en SicCatalogoCompras
    const buscarEnSicCatalogoCompras = async (emisorNumeroIdentificacion, codigoComercialCodigo) => {
        try {
            const q = query(
                collection(db, 'SicCatalogoCompras'),
                where('emisorNumeroIdentificacion', '==', emisorNumeroIdentificacion),
                where('codigoComercialCodigo', '==', codigoComercialCodigo)
            );
            
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
                const docData = querySnapshot.docs[0].data();
                return {
                    existe: true,
                    codigoEmpresa: docData.codigoEmpresa || null,
                    id: querySnapshot.docs[0].id
                };
            }
            
            return { existe: false, codigoEmpresa: null, id: null };
        } catch (error) {
            console.error('❌ Error al buscar en SicCatalogoCompras:', error);
            throw error;
        }
    };
    
    // Función para guardar en SicDetallesCompras
    const guardarEnSicDetallesCompras = async (documento, codigoEmpresa) => {
        try {
            // Verificar si ya existe un registro con estos datos
            const q = query(
                collection(db, 'SicDetallesCompras'),
                where('emisorNumeroIdentificacion', '==', documento.emisorNumeroIdentificacion),
                where('codigoComercialCodigo', '==', documento.codigoComercialCodigo),
                where('codigoEmpresa', '==', codigoEmpresa)
            );
            
            const querySnapshot = await getDocs(q);
            const fechaModificacion = new Date();
            
            if (querySnapshot.empty) {
                // Crear nuevo documento si no existe
                await addDoc(collection(db, 'SicDetallesCompras'), {
                    emisor: documento.emisorNombre,
                    emisorNumeroIdentificacion: documento.emisorNumeroIdentificacion,
                    codigoComercialCodigo: documento.codigoComercialCodigo,
                    codigoEmpresa: codigoEmpresa,
                    fechaActualizacion: fechaModificacion,
                    fechaModificacion: fechaModificacion,
                    detalle: documento.detalle || 'N/D',
                    numeroConsecutivo: documento.numeroConsecutivo || 'N/D'
                });
                
                //console.log('✅ Nuevo registro creado en SicDetallesCompras');
            } else {
                // Actualizar documento existente
                const docRef = doc(db, 'SicDetallesCompras', querySnapshot.docs[0].id);
                await updateDoc(docRef, {
                    fechaActualizacion: fechaModificacion,
                    fechaModificacion: fechaModificacion
                });
                //console.log('ℹ️ Registro actualizado en SicDetallesCompras');
            }
        } catch (error) {
            console.error('❌ Error al guardar en SicDetallesCompras:', error);
            throw error;
        }
    };

    // Cargar códigos comerciales desde SicDetalleFactura
    const cargarCodigosComerciales = useCallback(async () => {
        try {
            setLoadingCodigos(true);
            const snapshot = await getDocs(collection(db, 'SicDetalleFactura'));
            
            if (snapshot.empty) {
                setCodigosComerciales([]);
                setLoadingCodigos(false);
                return;
            }
            
            const codigos = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.codigoComercialCodigo) {
                    codigos.push({
                        id: doc.id,
                        codigoComercialCodigo: data.codigoComercialCodigo,
                        descripcion: data.detalle || 'Sin descripción',
                        codigo: data.codigo || 'N/D'
                    });
                }
            });
            
            // Eliminar duplicados
            const codigosUnicos = codigos.filter((codigo, index, self) =>
                index === self.findIndex(c => c.codigoComercialCodigo === codigo.codigoComercialCodigo)
            );
            
            setCodigosComerciales(codigosUnicos);
        } catch (error) {
            console.error('❌ Error al cargar códigos comerciales:', error);
            setError('Error al cargar códigos comerciales');
        } finally {
            setLoadingCodigos(false);
        }
    }, []);

    const handleMouseDown = (e) => {
        if (e.button !== 0 || !tableContainerRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - tableContainerRef.current.offsetLeft);
        setScrollLeft(tableContainerRef.current.scrollLeft);
        tableContainerRef.current.style.cursor = 'grabbing';
        tableContainerRef.current.style.userSelect = 'none';
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !tableContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - tableContainerRef.current.offsetLeft;
        const walk = (x - startX) * 2;
        tableContainerRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        if (tableContainerRef.current) {
            tableContainerRef.current.style.cursor = 'grab';
            tableContainerRef.current.style.userSelect = 'auto';
        }
    };

    const ordenarDocumentos = useCallback((documentos) => {
        return [...documentos].sort((a, b) => {
            let valorA = a[orden.campo] || '';
            let valorB = b[orden.campo] || '';

            if (['numeroConsecutivo', 'numeroLinea', 'precioUnitario', 'montoTotal', 'subTotal', 'impuestoMonto', 'montoTotalLinea', 'impuestoTarifa'].includes(orden.campo)) {
                valorA = Number(valorA) || 0;
                valorB = Number(valorB) || 0;
            }

            if (orden.campo === 'fechaEmision' && valorA !== 'N/D' && valorB !== 'N/D') {
                valorA = new Date(valorA);
                valorB = new Date(valorB);
            }

            if (valorA < valorB) {
                return orden.direccion === 'asc' ? -1 : 1;
            }
            if (valorA > valorB) {
                return orden.direccion === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [orden]);

    const cambiarOrden = (campo) => {
        setOrden(prevOrden => ({
            campo,
            direccion: prevOrden.campo === campo && prevOrden.direccion === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Abrir modal para seleccionar código comercial
    const abrirModalSeleccion = async (documento) => {
        try {
            setModalAbierto(true);
            // Crear una copia del documento para evitar problemas de referencia
            setDocumentoSeleccionado({...documento});
            setCodigoSeleccionado('');
            setFiltroCodigo('');
            
            // Cargar códigos comerciales si no están cargados
            if (codigosComerciales.length === 0) {
                setLoadingCodigos(true);
                await cargarCodigosComerciales();
            }
        } catch (error) {
            console.error('❌ Error al abrir modal:', error);
            setError('Error al abrir el modal de selección');
        }
    };

    const actualizarCodigoEmpresa = async () => {
        if (!documentoSeleccionado || !codigoSeleccionado) {
            setError('Por favor seleccione un código comercial');
            return;
        }
        
        // Validar que el documento todavía existe en la lista
        const documentoExiste = detalles.some(doc => doc.id === documentoSeleccionado.id);
        if (!documentoExiste) {
            setError('El documento ya no está disponible');
            setModalAbierto(false);
            return;
        }
        
        try {
            // Obtener fecha y hora actual
            const fechaModificacion = new Date();
            
            // 2. BUSCAR EN SICCATALOGOCOMPRAS Y ACTUALIZAR SOLO ESA TABLA
            const resultadoBusqueda = await buscarEnSicCatalogoCompras(
                documentoSeleccionado.emisorNumeroIdentificacion,
                documentoSeleccionado.codigoComercialCodigo
            );
            
            if (resultadoBusqueda.existe) {
                // ACTUALIZAR SICCATALOGOCOMPRAS con el valor seleccionado
                await updateDoc(doc(db, 'SicCatalogoCompras', resultadoBusqueda.id), {
                    codigoEmpresa: codigoSeleccionado,
                    fechaActualizacion: fechaModificacion,
                    fechaModificacion: fechaModificacion
                });
                
                //console.log('✅ SicCatalogoCompras actualizado con:', codigoSeleccionado);
            } else {
                // Si no existe en SicCatalogoCompras, crear nuevo registro
                await addDoc(collection(db, 'SicCatalogoCompras'), {
                    emisorNumeroIdentificacion: documentoSeleccionado.emisorNumeroIdentificacion,
                    codigoComercialCodigo: documentoSeleccionado.codigoComercialCodigo,
                    codigoEmpresa: codigoSeleccionado,
                    fechaCreacion: fechaModificacion,
                    fechaActualizacion: fechaModificacion,
                    fechaModificacion: fechaModificacion
                });
                
                //console.log('✅ Nuevo registro creado en SicCatalogoCompras con:', codigoSeleccionado);
            }
            
            // También actualizar DetalleFactura.codigoEmpresa con el valor seleccionado
            await updateDoc(doc(db, 'DetalleFactura', documentoSeleccionado.id), {
                codigoEmpresa: codigoSeleccionado,
                fechaModificacion: fechaModificacion
            });
            
            //console.log('✅ DetalleFactura.codigoEmpresa actualizado con:', codigoSeleccionado);
            
            // Guardar en la nueva tabla SicDetallesCompras
            await guardarEnSicDetallesCompras({
                ...documentoSeleccionado,
                fechaModificacion: fechaModificacion
            }, codigoSeleccionado);
            
            // Actualizar el estado local
            setDetalles(prevDetalles => 
                prevDetalles.map(doc => 
                    doc.id === documentoSeleccionado.id 
                        ? { 
                            ...doc, 
                            codigoEmpresa: codigoSeleccionado,
                            fechaModificacion: fechaModificacion
                        }
                        : doc
                )
            );
            
            setDocumentosActualizados(prev => prev + 1);
            setModalAbierto(false);
            setError(null);
            
            // Mostrar mensaje de éxito
            alert(`✅ Código actualizado correctamente a: ${codigoSeleccionado}`);
            
        } catch (error) {
            console.error('❌ Error al actualizar código de empresa:', error);
            setError(`Error al actualizar: ${error.message}`);
        }
    };

    const cargarDetalles = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            setDocumentosActualizados(0);
            
            const snapshot = await getDocs(collection(db, 'DetalleFactura'));
            
            if (snapshot.empty) {
                setStats({ total: 0, mostrados: 0 });
                setDetalles([]);
                setLoading(false);
                setFiltroActivo("No se encontraron documentos.");
                return;
            }
            
            const procesarYActualizarDocumento = async (docData, docId) => {
                const emisorNumeroIdentificacion = docData.emisor?.Identificacion?.Numero || 
                                                docData.emisorNumeroIdentificacion || 
                                                'N/D';
                
                const emisorNombre = docData.emisor?.Nombre || 
                                     docData.emisorNombre || 
                                     docData.emisor?.nombre || 
                                     'N/D';
                
                const receptorNumero = docData.receptor?.Identificacion?.Numero || 
                                       docData.receptor?.identificacion?.numero ||
                                       docData.receptorNumeroIdentificacion ||
                                       'N/D';
                
                const receptorNombre = docData.receptor?.Nombre || 
                                       docData.receptor?.nombre ||
                                       docData.receptorNombre ||
                                       'N/D';
                
                let fechaEmision = docData.documento?.FechaEmision || 
                                   docData.documento?.fechaEmision ||
                                   docData.fechaEmision ||
                                   'N/D';

                if (fechaEmision !== 'N/D' && typeof fechaEmision === 'string' && fechaEmision.length >= 10) {
                    fechaEmision = fechaEmision.substring(0, 10);
                }

                let codigoEmpresa = docData.codigoEmpresa || 'N/D';
                let codigoComercialCodigo = docData.codigoComercialCodigo || 'N/D';
                let necesitaActualizacion = false;
                let nuevoCodigoEmpresa = codigoEmpresa;

                // SOLO PROCESAR DOCUMENTOS CON codigoEmpresa = "XXXYYYZZZ123"
                if (codigoEmpresa === "XXXYYYZZZ123" && codigoComercialCodigo !== 'N/D') {
                    try {
                        const resultadoBusqueda = await buscarEnSicCatalogoCompras(
                            emisorNumeroIdentificacion,
                            codigoComercialCodigo
                        );
                        
                        if (resultadoBusqueda.existe && resultadoBusqueda.codigoEmpresa) {
                            nuevoCodigoEmpresa = resultadoBusqueda.codigoEmpresa;
                            necesitaActualizacion = true;
                            //console.log('✅ Encontrado en SicCatalogoCompras:', nuevoCodigoEmpresa);
                        }
                    } catch (error) {
                        console.error('❌ Error al buscar en SicCatalogoCompras:', error);
                    }
                }

                // Actualizar el documento en Firebase si es necesario
                if (necesitaActualizacion) {
                    try {
                        const fechaModificacion = new Date();
                        
                        await updateDoc(doc(db, 'DetalleFactura', docId), {
                            codigoEmpresa: nuevoCodigoEmpresa,
                            fechaModificacion: fechaModificacion
                        });
                        //console.log('✅ Documento actualizado en Firebase:', docId);
                        setDocumentosActualizados(prev => prev + 1);
                        
                        // Guardar en la nueva tabla SicDetallesCompras
                        await guardarEnSicDetallesCompras({
                            ...docData,
                            emisorNumeroIdentificacion,
                            emisorNombre,
                            receptorNumeroIdentificacion: receptorNumero,
                            receptorNombre,
                            fechaEmision,
                            codigoEmpresa: nuevoCodigoEmpresa
                        }, nuevoCodigoEmpresa);
                    } catch (updateError) {
                        console.error('❌ Error al actualizar documento:', docId, updateError);
                    }
                }

                return {
                    ...docData,
                    emisorNumeroIdentificacion,
                    emisorNombre,
                    receptorNumeroIdentificacion: receptorNumero,
                    receptorNombre,
                    fechaEmision,
                    codigoEmpresa: nuevoCodigoEmpresa,
                    // Solo devolver documentos que originalmente tenían codigoEmpresa = "XXXYYYZZZ123"
                    // o los que fueron actualizados pero deben mostrarse
                    _mostrar: codigoEmpresa === "XXXYYYZZZ123"
                };
            };

            // Procesar todos los documentos
            const documentosProcesados = [];
            for (const docSnapshot of snapshot.docs) {
                const docData = docSnapshot.data();
                // Solo procesar documentos con codigoEmpresa = "XXXYYYZZZ123"
                if (docData.codigoEmpresa === "XXXYYYZZZ123") {
                    const processedDoc = await procesarYActualizarDocumento(docData, docSnapshot.id);
                    if (processedDoc) {
                        documentosProcesados.push({
                            id: docSnapshot.id,
                            ...processedDoc
                        });
                    }
                }
            }

            // Filtrar SOLO los documentos con codigoEmpresa = "XXXYYYZZZ123"
            const documentosFiltrados = documentosProcesados.filter(doc => 
                doc.codigoEmpresa === "XXXYYYZZZ123" || doc._mostrar
            );

            const filtroTexto = "Filtrando por: codigoEmpresa = 'XXXYYYZZZ123'";

            const documentosOrdenados = ordenarDocumentos(documentosFiltrados);
            setDetalles(documentosOrdenados);
            setStats({ 
                total: snapshot.size, 
                mostrados: documentosOrdenados.length 
            });
            setFiltroActivo(filtroTexto);

        } catch (error) {
            console.error('❌ Error al cargar detalles:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [ordenarDocumentos]); // <<-- Aquí se ha eliminado 'identificacion'

    useEffect(() => {
        cargarDetalles();
    }, [cargarDetalles]);

    const formatearValor = (valor, key) => {
        if (valor === undefined || valor === null || valor === '') return 'N/D';
        if (valor === 'N/D' || valor === 'null' || valor === 'undefined') return 'N/D';
        if (key === 'fechaEmision' && typeof valor === 'string' && valor.length >= 10) {
            const [año, mes, día] = valor.split('-');
            if (año && mes && día) return `${día}/${mes}/${año}`;
        }
        if (typeof valor === 'number') return valor.toLocaleString('es-CR');
        return valor.toString();
    };

    // Función para renderizar el contenido de la celda codigoEmpresa
    const renderCodigoEmpresa = (fila) => {
        if (fila.codigoEmpresa === "XXXYYYZZZ123") {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                    <span style={{ color: '#dc3545', fontWeight: 'bold' }}>{fila.codigoEmpresa}</span>
                    <button 
                        onClick={() => abrirModalSeleccion(fila)}
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Catalogar
                    </button>
                </div>
            );
        }
        
        return formatearValor(fila.codigoEmpresa, 'codigoEmpresa');
    };

    // Filtrar códigos comerciales según el texto de búsqueda
    const codigosFiltrados = codigosComerciales.filter(codigo => 
        codigo.codigoComercialCodigo.toLowerCase().includes(filtroCodigo.toLowerCase()) ||
        codigo.descripcion.toLowerCase().includes(filtroCodigo.toLowerCase())
    );

    const exportarExcel = () => {
        if (detalles.length === 0) return;
        
        const datosExcel = detalles.map(doc => {
            const fila = {};
            estructuraCampos.forEach(campo => {
                fila[campo.label] = formatearValor(doc[campo.key]);
            });
            return fila;
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(datosExcel);
        
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Detalles Factura');
        
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'detalles_factura.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const recargarDatos = () => {
        cargarDetalles();
    };

    const cerrarModal = () => {
        setModalAbierto(false);
        setDocumentoSeleccionado(null);
        setCodigoSeleccionado('');
        setFiltroCodigo('');
        setError(null);
    };

    if (loading) {
        return (
            <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                minHeight: '300px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
                <h3 style={{ color: '#495057', marginBottom: '10px' }}>Cargando datos...</h3>
                <p style={{ color: '#6c757d' }}>Filtrando por codigoEmpresa = "XXXYYYZZZ123"</p>
                {documentosActualizados > 0 && (
                    <p style={{ color: '#28a745', marginTop: '10px' }}>
                        ✅ {documentosActualizados} documentos actualizados en Firebase
                    </p>
                )}
            </div>
        );
    }

    if (error && !modalAbierto) {
        return (
            <div style={{ 
                padding: '40px', 
                textAlign: 'center',
                backgroundColor: '#f8d7da',
                border: '1px solid #f5c6cb',
                borderRadius: '8px',
                margin: '20px'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
                <h3 style={{ color: '#721c24', marginBottom: '15px' }}>Error</h3>
                <p style={{ color: '#721c24', marginBottom: '20px' }}>{error}</p>
                <button 
                    onClick={recargarDatos}
                    style={{
                        padding: '12px 24px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                    }}
                >
                    🔄 Reintentar
                </button>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '100%', margin: '0 auto' }}>
            {/* Modal para seleccionar código comercial */}
            {modalAbierto && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '25px',
                        borderRadius: '12px',
                        width: '95%',
                        maxWidth: '1200px',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <h2 style={{ marginBottom: '20px', color: '#2c3e50' }}>
                            Seleccionar Código Comercial
                        </h2>
                        
                        {error && (
                            <div style={{
                                backgroundColor: '#f8d7da',
                                color: '#721c24',
                                padding: '10px',
                                borderRadius: '4px',
                                marginBottom: '15px'
                            }}>
                                {error}
                            </div>
                        )}
                        
                        <div style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '15px', 
                            borderRadius: '8px',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{ color: '#495057', marginBottom: '15px' }}>Detalles del Documento</h4>
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                                <div>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>N° Consecutivo:</strong> {documentoSeleccionado?.numeroConsecutivo || 'N/D'}</p>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Fecha Emisión:</strong> {formatearValor(documentoSeleccionado?.fechaEmision, 'fechaEmision')}</p>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>N° Línea:</strong> {documentoSeleccionado?.numeroLinea || 'N/D'}</p>
                                </div>
                                
                                <div>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Emisor ID:</strong> {documentoSeleccionado?.emisorNumeroIdentificacion || 'N/D'}</p>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Emisor Nombre:</strong> {documentoSeleccionado?.emisorNombre || 'N/D'}</p>
                                </div>
                                
                                <div>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Receptor ID:</strong> {documentoSeleccionado?.receptorNumeroIdentificacion || 'N/D'}</p>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Receptor Nombre:</strong> {documentoSeleccionado?.receptorNombre || 'N/D'}</p>
                                </div>
                                
                                <div>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Monto Total:</strong> {formatearValor(documentoSeleccionado?.montoTotal, 'montoTotal')}</p>
                                    <p style={{ margin: '5px 0', fontSize: '14px' }}><strong>Código Comercial:</strong> {documentoSeleccionado?.codigoComercialCodigo || 'N/D'}</p>
                                </div>
                            </div>
                            
                            {/* Detalle expandido */}
                            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                                <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>Detalle completo:</p>
                                <p style={{ margin: 0, wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                    {documentoSeleccionado?.detalle || 'N/D'}
                                </p>
                            </div>
                        </div>
                        
                        {/* Mostrar descripción del código seleccionado arriba de la tabla */}
                        {codigoSeleccionado && (
                            <div style={{ 
                                marginBottom: '20px', 
                                padding: '15px', 
                                backgroundColor: '#e8f5e8', 
                                borderRadius: '8px',
                                border: '1px solid #c3e6cb'
                            }}>
                                <h4 style={{ color: '#155724', margin: '0 0 10px 0' }}>Código seleccionado:</h4>
                                <p style={{ margin: '5px 0', fontWeight: 'bold' }}>{codigoSeleccionado}</p>
                                <p style={{ margin: '5px 0' }}>
                                    {codigosComerciales.find(c => c.codigoComercialCodigo === codigoSeleccionado)?.descripcion || 'Sin descripción'}
                                </p>
                            </div>
                        )}
                        
                        <div style={{ marginBottom: '20px' }}>
                            <input
                                type="text"
                                placeholder="Buscar código o descripción..."
                                value={filtroCodigo}
                                onChange={(e) => setFiltroCodigo(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    fontSize: '16px'
                                }}
                            />
                        </div>
                        
                        {loadingCodigos ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <p>Cargando códigos comerciales...</p>
                            </div>
                        ) : codigosFiltrados.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                <p>No se encontraron códigos comerciales</p>
                            </div>
                        ) : (
                            <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '20px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', width: '50px' }}>Seleccionar</th>
                                            <th style={{ padding: '10px', border: '1px solid ' +
                                            '#ddd', textAlign: 'left' }}>Código</th>
                                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>CAByS</th>
                                            <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left' }}>Descripción</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {codigosFiltrados.map(codigo => (
                                            <tr key={codigo.id} style={{ borderBottom: '1px solid #ddd' }}>
                                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                                    <input
                                                        type="radio"
                                                        name="codigoComercial"
                                                        value={codigo.codigoComercialCodigo}
                                                        checked={codigoSeleccionado === codigo.codigoComercialCodigo}
                                                        onChange={() => setCodigoSeleccionado(codigo.codigoComercialCodigo)}
                                                    />
                                                </td>
                                                <td style={{ padding: '10px', fontWeight: 'bold' }}>{codigo.codigoComercialCodigo}</td>
                                                <td style={{ padding: '10px' }}>{codigo.codigo || 'N/D'}</td>
                                                <td style={{ padding: '10px' }}>{codigo.descripcion}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={cerrarModal}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={actualizarCodigoEmpresa}
                                disabled={!codigoSeleccionado}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: !codigoSeleccionado ? '#6c757d' : '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: !codigoSeleccionado ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Confirmar Selección
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ 
                backgroundColor: 'white', 
                padding: '25px', 
                borderRadius: '12px', 
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                marginBottom: '25px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                        <h1 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            📊 Catalogar Productos
                        </h1>
                        <p style={{ color: '#6c757d', margin: 0 }}>
                            Filtro actual: <strong>{filtroActivo}</strong>
                        </p>
                        {identificacion && (
                            <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: '14px' }}>
                                Para emisor: <strong>{identificacion}</strong> se reemplaza codigoEmpresa por codigoComercialCodigo
                            </p>
                        )}
                        {documentosActualizados > 0 && (
                            <p style={{ color: '#28a745', margin: '5px 0 0 0', fontSize: '14px' }}>
                                ✅ <strong>{documentosActualizados} documentos</strong> actualizados en Firebase
                            </p>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button 
                            onClick={exportarExcel}
                            disabled={detalles.length === 0}
                            style={{
                                padding: '12px 20px',
                                backgroundColor: detalles.length === 0 ? '#6c757d' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: detalles.length === 0 ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            📊 Exportar Excel
                        </button>
                        
                        <button 
                            onClick={recargarDatos}
                            style={{
                                padding: '12px 20px',
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 'bold'
                            }}
                        >
                            🔄 Actualizar
                        </button>
                    </div>
                </div>

                <div style={{ 
                    backgroundColor: '#e8f5e8', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid #c3e6cb'
                }}>
                    <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                        <div>
                            <h4 style={{ color: '#155724', margin: '0 0 5px 0' }}>📈 Estadísticas</h4>
                            <p style={{ color: '#155724', margin: 0, fontWeight: 'bold' }}>
                                Se encontraron {detalles.length} documentos con codigoEmpresa = "XXXYYYZZZ123"
                            </p>
                        </div>
                        <div style={{ 
                            padding: '8px 16px', 
                            backgroundColor: '#155724', 
                            color: 'white',
                            borderRadius: '20px',
                            fontWeight: 'bold',
                            fontSize: '14px'
                        }}>
                            ✅ Filtro activo
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '12px', 
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {detalles.length === 0 ? (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '60px 20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '2px dashed #dee2e6'
                    }}>
                        <div style={{ fontSize: '64px', marginBottom: '20px' }}>📭</div>
                        <h3 style={{ color: '#6c757d', marginBottom: '10px' }}>No se encontraron documentos</h3>
                        <p style={{ color: '#adb5bd' }}>
                            No hay registros con codigoEmpresa = "XXXYYYZZZ123"
                        </p>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ color: '#2d3748', margin: '0 0 10px 0' }}>
                                📋 Resultados ({detalles.length} registros)
                            </h3>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span style={{ color: '#6c757d', fontSize: '14px' }}>Ordenar por:</span>
                                <select 
                                    value={orden.campo}
                                    onChange={(e) => cambiarOrden(e.target.value)}
                                    style={{
                                        padding: '8px 12px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <option value="fechaEmision">Fecha Emisión</option>
                                    <option value="numeroConsecutivo">N° Consecutivo</option>
                                    <option value="codigoEmpresa">Código Empresa</option>
                                    <option value="emisorNumeroIdentificacion">Emisor ID</option>
                                </select>
                                <button 
                                    onClick={() => cambiarOrden(orden.campo)}
                                    style={{
                                        padding: '8px 12px',
                                        backgroundColor: '#edf2f7',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '6px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {orden.direccion === 'asc' ? '↑ Asc' : '↓ Desc'}
                                </button>
                            </div>
                        </div>

                        <p style={{ color: '#6c757d', margin: '0 0 15px 0', fontSize: '14px' }}>
                            💡 <strong>Arrastre con el mouse</strong> para navegar horizontalmente ← →
                        </p>

                        <div 
                            ref={tableContainerRef}
                            style={{ 
                                overflowX: 'auto',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                cursor: 'grab',
                                maxHeight: '70vh'
                            }}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse', 
                                minWidth: '2500px'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#edf2f7' }}>
                                        <th style={{ 
                                            padding: '12px', 
                                            border: '1px solid #e2e8f0', 
                                            fontWeight: 'bold', 
                                            color: '#4a5568',
                                            textAlign: 'left',
                                            position: 'sticky',
                                            left: 0,
                                            backgroundColor: '#edf2f7',
                                            zIndex: 10,
                                            width: '80px'
                                        }}>
                                            #
                                        </th>
                                        {estructuraCampos.map(campo => (
                                            <th 
                                                key={campo.key}
                                                style={{ 
                                                    padding: '12px', 
                                                    border: '1px solid #e2e8f0', 
                                                    fontWeight: 'bold', 
                                                    color: '#4a5568',
                                                    textAlign: 'left',
                                                    minWidth: campo.width,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {campo.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {detalles.map((fila, index) => (
                                        <tr key={fila.id} style={{ 
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                                        }}>
                                            <td style={{ 
                                                padding: '12px', 
                                                border: '1px solid #e2e8f0',
                                                fontWeight: 'bold',
                                                color: '#4a5568',
                                                textAlign: 'center',
                                                position: 'sticky',
                                                left: 0,
                                                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                zIndex: 5
                                            }}>
                                                {index + 1}
                                            </td>
                                            {estructuraCampos.map(campo => (
                                                <td 
                                                    key={campo.key}
                                                    style={{ 
                                                        padding: '12px', 
                                                        border: '1px solid #e2e8f0',
                                                        color: '#2d3748',
                                                        fontSize: '14px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {campo.key === 'codigoEmpresa' 
                                                        ? renderCodigoEmpresa(fila)
                                                        : formatearValor(fila[campo.key], campo.key)
                                                    }
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            <style>
                {`
                .grabbing {
                    cursor: grabbing !important;
                    user-select: none !important;
                }
                
                ::-webkit-scrollbar {
                    height: 8px;
                }
                
                ::-webkit-scrollbar-track {
                    background: 'f7fafc';
                    border-radius: 4px;
                }
                
                ::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 4px;
                }
                
                ::-webkit-scrollbar-thumb:hover {
                    background: #a0aec0;
                }
                `}
            </style>
        </div>
    );
};

export default TablaDetalles;